import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/api/client'
import { useT } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'
import { mobileUsersMessages } from '@/i18n/messages/mobileUsers'
import { Select } from '@/components/Select'
import { ConfirmButton, ErrorLine, Modal, btnPrimary, btnSecondary, inputCls } from '@/features/users/parts'
import { Field } from '@/features/users/EditUserDialog'
import { TITLES, fullName, suggestUsername, type MobileEmployeeHit, type MobileOptions, type MobileUser } from './types'

const errText = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback)

type Form = {
  name: string; surname: string; username: string; email: string; workTitle: string
  personalNumber: string; titleSelector: number; countryId: string; employeeId: string
  rtkLoginId: string; lidarEnabled: boolean; sendVideo: boolean; canAddCar: boolean
  canAddMachine: boolean; isAsfalt: number; active: boolean
}

function fromUser(u: MobileUser): Form {
  return {
    name: u.name ?? '', surname: u.surname ?? '', username: u.username ?? '', email: u.email ?? '',
    workTitle: u.workTitle ?? '', personalNumber: u.personalNumber ?? '', titleSelector: u.titleSelector,
    countryId: u.countryId, employeeId: u.employeeId && u.employeeId !== '00000000-0000-0000-0000-000000000000' ? u.employeeId : '',
    rtkLoginId: u.rtkLoginId ?? '', lidarEnabled: u.lidarEnabled, sendVideo: u.sendVideo,
    canAddCar: u.canAddCar, canAddMachine: u.canAddMachine, isAsfalt: u.isAsfalt, active: u.active,
  }
}

export function EditMobileUserDialog({ user, options, onClose }: { user: MobileUser; options: MobileOptions; onClose: () => void }) {
  const qc = useQueryClient()
  const t = useT(mobileUsersMessages)
  const tc = useT(commonMessages)
  const [form, setForm] = useState<Form>(fromUser(user))
  const [error, setError] = useState<string | null>(null)
  const [password, setPassword] = useState<string | null>(null)
  const [empQ, setEmpQ] = useState('')
  const [employee, setEmployee] = useState<MobileEmployeeHit | null>(
    user.employeeName ? { id: user.employeeId, name: user.employeeName, number: user.employeeNumber, company: null } : null)

  const hits = useQuery<MobileEmployeeHit[]>({
    queryKey: ['mobile-employees', empQ],
    queryFn: () => api.get<MobileEmployeeHit[]>(`/api/mobile-users/employees?q=${encodeURIComponent(empQ)}`),
    enabled: empQ.trim().length >= 2,
  })

  const save = useMutation({
    mutationFn: () => api.put(`/api/mobile-users/${user.workerId}`, {
      name: form.name, surname: form.surname, username: form.username, email: form.email || null,
      workTitle: form.workTitle, personalNumber: form.personalNumber, titleSelector: form.titleSelector,
      countryId: form.countryId, employeeId: employee?.id || form.employeeId || null,
      rtkLoginId: form.rtkLoginId || null, lidarEnabled: form.lidarEnabled, sendVideo: form.sendVideo,
      canAddCar: form.canAddCar, canAddMachine: form.canAddMachine, isAsfalt: form.isAsfalt, active: form.active,
    }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['mobile-users'] }); onClose() },
    onError: (e) => setError(errText(e, t('couldNotSave'))),
  })
  const reset = useMutation({
    mutationFn: () => api.post<{ password: string }>(`/api/mobile-users/${user.workerId}/password`),
    onSuccess: (r) => { setPassword(r.password); void qc.invalidateQueries({ queryKey: ['mobile-users'] }) },
    onError: (e) => setError(errText(e, t('couldNotSave'))),
  })
  const active = useMutation({
    mutationFn: (on: boolean) => api.post(`/api/mobile-users/${user.workerId}/active`, { active: on }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['mobile-users'] }); onClose() },
    onError: (e) => setError(errText(e, t('couldNotSave'))),
  })

  const set = (patch: Partial<Form>) => setForm((f) => ({ ...f, ...patch }))
  const writes = options.enabled

  return (
    <Modal onClose={onClose} wide title={t('editUser')} subtitle={fullName(user) || user.username || '—'}
      footer={<>
        <ErrorLine error={error} />
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className={btnSecondary}>{tc('cancel')}</button>
          <button type="button" onClick={() => { setError(null); save.mutate() }} disabled={!writes || save.isPending} className={btnPrimary}>
            {save.isPending ? t('creating') : tc('save')}
          </button>
        </div>
      </>}>
      {!writes && <p className="text-sm text-neutral-500">{t('writesOff')}</p>}
      {password && <ShownPassword password={password} />}
      <Fields form={form} set={set} options={options} employee={employee} setEmployee={setEmployee} setEmpQ={setEmpQ} hits={hits.data} />
      {writes && (
        <div className="flex flex-wrap gap-2 border-t border-neutral-200 pt-4 dark:border-neutral-800">
          <ConfirmButton label={t('resetPassword')} question={t('resetConfirm', { name: fullName(user) || user.username || '' })}
            onConfirm={() => { setError(null); reset.mutate() }} busy={reset.isPending} />
          {form.active
            ? <ConfirmButton danger label={t('switchOff')} question={t('switchOffConfirm', { name: fullName(user) || user.username || '' })}
                onConfirm={() => { setError(null); active.mutate(false) }} busy={active.isPending} />
            : <ConfirmButton label={t('switchOn')} question={t('switchOn')}
                onConfirm={() => { setError(null); active.mutate(true) }} busy={active.isPending} />}
        </div>
      )}
    </Modal>
  )
}

export function CreateMobileUserDialog({ options, translation, onClose }: { options: MobileOptions; translation?: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const t = useT(mobileUsersMessages)
  const tc = useT(commonMessages)
  const defaultCountry = options.countries[0]?.id ?? ''
  const [form, setForm] = useState<Form>({
    name: '', surname: '', username: '', email: '', workTitle: translation ? 'Translation only' : '',
    personalNumber: '', titleSelector: translation ? 71 : 0, countryId: defaultCountry, employeeId: '',
    rtkLoginId: '', lidarEnabled: false, sendVideo: false, canAddCar: false, canAddMachine: false,
    isAsfalt: 0, active: true,
  })
  const [usernameTouched, setUsernameTouched] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<{ username: string; password: string } | null>(null)
  const [empQ, setEmpQ] = useState('')
  const [employee, setEmployee] = useState<MobileEmployeeHit | null>(null)

  const setName = (patch: Partial<Form>) => setForm((f) => {
    const next = { ...f, ...patch }
    return usernameTouched || translation ? next : { ...next, username: suggestUsername(next.name, next.surname) }
  })

  const hits = useQuery<MobileEmployeeHit[]>({
    queryKey: ['mobile-employees', empQ],
    queryFn: () => api.get<MobileEmployeeHit[]>(`/api/mobile-users/employees?q=${encodeURIComponent(empQ)}`),
    enabled: !translation && empQ.trim().length >= 2,
  })

  const create = useMutation({
    mutationFn: () => translation
      ? api.post<{ workerId: string; username: string; password: string }>('/api/mobile-users/translation', {
          name: form.name, surname: form.surname, email: form.email,
        })
      : api.post<{ workerId: string; username: string; password: string }>('/api/mobile-users/', {
          name: form.name, surname: form.surname, username: form.username, email: form.email || null,
          workTitle: form.workTitle, personalNumber: form.personalNumber, titleSelector: form.titleSelector,
          countryId: form.countryId, employeeId: employee?.id || null, rtkLoginId: form.rtkLoginId || null,
          lidarEnabled: form.lidarEnabled, sendVideo: form.sendVideo, canAddCar: form.canAddCar,
          canAddMachine: form.canAddMachine, isAsfalt: form.isAsfalt,
        }),
    onSuccess: (r) => { setCreated({ username: r.username, password: r.password }); void qc.invalidateQueries({ queryKey: ['mobile-users'] }) },
    onError: (e) => setError(errText(e, t('couldNotSave'))),
  })

  if (created)
    return (
      <Modal onClose={onClose} title={t('credentialsTitle')}
        footer={<><span /><button type="button" onClick={onClose} className={btnPrimary}>{t('done')}</button></>}>
        <ShownPassword username={created.username} password={created.password} />
      </Modal>
    )

  const ready = form.name.trim() && form.surname.trim() && (translation ? form.email.trim() : form.username.trim().length >= 3 && form.countryId)
  return (
    <Modal onClose={onClose} wide
      title={translation ? t('newTranslation') : t('newUser')}
      subtitle={translation ? t('translationHint') : t('createHint')}
      footer={<>
        <ErrorLine error={error} />
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className={btnSecondary}>{tc('cancel')}</button>
          <button type="button" onClick={() => { setError(null); create.mutate() }} disabled={!ready || create.isPending} className={btnPrimary}>
            {create.isPending ? t('creating') : t('create')}
          </button>
        </div>
      </>}>
      {translation ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label={t('firstName')}><input className={inputCls} value={form.name} autoFocus onChange={(e) => setName({ name: e.target.value })} /></Field>
          <Field label={t('lastName')}><input className={inputCls} value={form.surname} onChange={(e) => setName({ surname: e.target.value })} /></Field>
          <Field label={t('email')}><input className={inputCls} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
        </div>
      ) : (
        <Fields form={form} set={(p) => {
          if (p.name !== undefined || p.surname !== undefined) setName(p)
          else setForm((f) => ({ ...f, ...p }))
        }} options={options} employee={employee} setEmployee={setEmployee} setEmpQ={setEmpQ} hits={hits.data}
          onUsername={() => setUsernameTouched(true)} creating />
      )}
    </Modal>
  )
}

function Fields({ form, set, options, employee, setEmployee, setEmpQ, hits, onUsername, creating }: {
  form: Form; set: (p: Partial<Form>) => void; options: MobileOptions
  employee: MobileEmployeeHit | null; setEmployee: (e: MobileEmployeeHit | null) => void
  setEmpQ: (q: string) => void; hits: MobileEmployeeHit[] | undefined
  onUsername?: () => void; creating?: boolean
}) {
  const t = useT(mobileUsersMessages)
  const titles = TITLES.filter((x) => options.titles.includes(x.id) || x.id === form.titleSelector)
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label={t('firstName')}><input className={inputCls} value={form.name} onChange={(e) => set({ name: e.target.value })} /></Field>
        <Field label={t('lastName')}><input className={inputCls} value={form.surname} onChange={(e) => set({ surname: e.target.value })} /></Field>
        <Field label={t('username')} {...(creating ? { hint: t('usernameHint') } : {})}>
          <input className={`${inputCls} font-mono`} value={form.username} autoCapitalize="none" spellCheck={false}
                 onChange={(e) => { onUsername?.(); set({ username: e.target.value.trim() }) }} />
        </Field>
        <Field label={t('email')}><input className={inputCls} type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} /></Field>
        <Field label={t('personalNumber')}><input className={inputCls} value={form.personalNumber} onChange={(e) => set({ personalNumber: e.target.value })} /></Field>
        <Field label={t('workTitle')}><input className={inputCls} value={form.workTitle} onChange={(e) => set({ workTitle: e.target.value })} /></Field>
        <Field label={t('country')}>
          <Select value={form.countryId} onChange={(v) => set({ countryId: v })}
            options={options.countries.map((c) => ({ value: c.id, label: c.name }))} />
        </Field>
        <Field label={t('position')}>
          <Select value={String(form.titleSelector)} onChange={(v) => set({ titleSelector: Number(v) })}
            options={titles.map((x) => ({ value: String(x.id), label: t(x.key) }))} />
        </Field>
        <Field label={t('employee')}>
          <Select
            value={employee?.id ?? form.employeeId}
            onChange={(id) => {
              if (!id) { setEmployee(null); set({ employeeId: '' }); return }
              const hit = (hits ?? []).find((h) => h.id === id) ?? (employee?.id === id ? employee : null)
              setEmployee(hit)
              set({ employeeId: id })
            }}
            options={(employee ? [employee] : []).concat(hits ?? [])
              .filter((h, i, a) => a.findIndex((x) => x.id === h.id) === i)
              .map((h) => ({ value: h.id, label: [h.number, h.name, h.company].filter(Boolean).join(' · ') }))}
            empty={{ value: '', label: t('none') }}
            placeholder={t('pickEmployee')}
            onQuery={setEmpQ}
          />
        </Field>
        <Field label={t('rtkLogin')}>
          <Select value={form.rtkLoginId} onChange={(v) => set({ rtkLoginId: v })}
            empty={{ value: '', label: t('rtkNone') }}
            options={options.rtkLogins.map((r) => ({ value: r.id, label: r.name }))} />
        </Field>
        <Field label={t('asphaltBl')}>
          <Select value={String(form.isAsfalt)} onChange={(v) => set({ isAsfalt: Number(v) })}
            options={[{ value: '0', label: t('none') }, { value: '1', label: t('asphalt') }]} />
        </Field>
      </div>
      <div className="grid gap-2 text-sm">
        {!creating && <label className="flex items-center gap-2"><input type="checkbox" checked={form.active} onChange={(e) => set({ active: e.target.checked })} />{t('active')}</label>}
        <label className="flex items-center gap-2"><input type="checkbox" checked={form.lidarEnabled} onChange={(e) => set({ lidarEnabled: e.target.checked })} />{t('lidar')}</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={form.sendVideo} onChange={(e) => set({ sendVideo: e.target.checked })} />{t('sendVideo')}</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={form.canAddCar} onChange={(e) => set({ canAddCar: e.target.checked })} />{t('canAddCar')}</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={form.canAddMachine} onChange={(e) => set({ canAddMachine: e.target.checked })} />{t('canAddMachine')}</label>
      </div>
    </div>
  )
}

function ShownPassword({ username, password }: { username?: string; password: string }) {
  const t = useT(mobileUsersMessages)
  const [copied, setCopied] = useState(false)
  const text = username ? `${t('username')}: ${username}\n${t('password')}: ${password}` : password
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000) } catch { /* selectable */ }
  }
  return (
    <div className="space-y-2 rounded-xl bg-amber-50 p-4 ring-1 ring-amber-200 dark:bg-amber-500/10 dark:ring-amber-500/30">
      <dl className="grid grid-cols-[auto_1fr] items-baseline gap-x-3 gap-y-1">
        {username && <><dt className="text-xs text-neutral-500">{t('username')}</dt><dd className="select-all font-mono text-base">{username}</dd></>}
        <dt className="text-xs text-neutral-500">{t('password')}</dt>
        <dd className="select-all font-mono text-xl tracking-wider">{password}</dd>
      </dl>
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-amber-900 dark:text-amber-200">{t('credentialsHint')}</p>
        <button type="button" onClick={copy} className={btnSecondary}>{copied ? t('copied') : t('copy')}</button>
      </div>
    </div>
  )
}

